import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Loader2, ShieldCheck, CheckCircle2, Upload, Camera,
  Check, X, FileText, Paperclip, User, Briefcase, FileCheck2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { API } from "../utils/api";

const DAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" }, { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const EXPERIENCE_LEVELS = [
  { value: "none",        label: "No experience yet" },
  { value: "under_1",     label: "Less than 1 year" },
  { value: "1_2_years",   label: "1–2 years" },
  { value: "3_5_years",   label: "3–5 years" },
  { value: "5_10_years",  label: "5–10 years" },
  { value: "10_plus",     label: "10+ years" },
];

const DOCUMENT_TYPES = [
  { key: "ndis_screening",   label: "NDIS Worker Screening Check" },
  { key: "ndis_orientation", label: "NDIS Worker Orientation Module" },
  { key: "wwcc",             label: "Working with Children Check" },
  { key: "police_check",     label: "Police Check" },
  { key: "first_aid",        label: "First Aid & CPR Certificate" },
];

const STEPS = [
  { key: "about",     title: "About you",     icon: User,       hint: "Who you are" },
  { key: "work",      title: "Your work",     icon: Briefcase,  hint: "Experience, availability & insurance" },
  { key: "documents", title: "Documents",     icon: FileCheck2, hint: "Which documents you have" },
];

export default function WorkerCreateAccountPage() {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    // About
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    speaks_other_language: null,
    other_language: "",
    photo_data_url: "",
    email: "",
    phone: "",
    address_line: "",
    suburb: "",
    state: "",
    postcode: "",
    // Work
    bio: "",
    experience_level: "",
    qualifications: "",
    availability_days: [],
    availability_notes: "",
    has_abn: null,
    abn: "",
    has_professional_indemnity: null,
    has_public_liability: null,
    consent_listing: false,
    notes: "",
    // Docs
    documents: DOCUMENT_TYPES.reduce((acc, d) => {
      acc[d.key] = { status: "" };
      return acc;
    }, {}),
  });
  const [errors, setErrors] = useState({});

  useEffect(() => { document.title = "Register your worker profile · BTR Health"; }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDay = (key) => {
    setForm((f) => ({
      ...f,
      availability_days: f.availability_days.includes(key)
        ? f.availability_days.filter((d) => d !== key)
        : [...f.availability_days, key],
    }));
  };

  const setDoc = (key, patch) => {
    setForm((f) => ({
      ...f,
      documents: { ...f.documents, [key]: { ...f.documents[key], ...patch } },
    }));
  };

  const onPhotoPick = (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => update("photo_data_url", reader.result);
    reader.readAsDataURL(file);
  };

  const validateStep = (n) => {
    const e = {};
    if (n === 0) {
      if (!form.first_name.trim()) e.first_name = "Required";
      if (!form.last_name.trim()) e.last_name = "Required";
      if (!form.date_of_birth) e.date_of_birth = "Required";
      if (!form.email) e.email = "Required";
      else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Invalid email";
      if (!form.phone.trim()) e.phone = "Required";
      else if (form.phone.replace(/\D/g, "").length < 9) e.phone = "Invalid mobile";
      if (!form.address_line.trim()) e.address_line = "Required";
      if (!form.suburb.trim()) e.suburb = "Required";
      if (!form.state) e.state = "Required";
      if (!form.postcode.trim()) e.postcode = "Required";
    }
    if (n === 1) {
      // bio is now optional
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validateStep(step)) { toast.error("Please fill in the required fields"); return; }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = async () => {
    // Final safety check across all steps
    for (let i = 0; i < STEPS.length - 1; i++) {
      if (!validateStep(i)) { setStep(i); toast.error("Some fields need attention"); return; }
    }
    if (!form.consent_listing) {
      toast.error("Please tick the consent box to be listed on BTR Health");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        date_of_birth: form.date_of_birth,
        gender: form.gender,
        languages: form.speaks_other_language && form.other_language.trim()
          ? `English, ${form.other_language.trim()}`
          : "English",
        speaks_other_language: !!form.speaks_other_language,
        other_language: form.other_language.trim(),
        address_line: form.address_line,
        location: form.suburb,
        suburb: form.suburb,
        state: form.state,
        postcode: form.postcode,
        photo_data_url: form.photo_data_url,
        bio: form.bio,
        experience_level: form.experience_level,
        qualifications: form.qualifications,
        availability_days: form.availability_days,
        availability_notes: form.availability_notes,
        has_abn: form.has_abn,
        abn: form.abn,
        has_professional_indemnity: form.has_professional_indemnity,
        has_public_liability: form.has_public_liability,
        consent_listing: form.consent_listing,
        notes: form.notes,
        documents: Object.entries(form.documents)
          .filter(([, v]) => v.status)
          .map(([key, v]) => ({
            key,
            label: DOCUMENT_TYPES.find((d) => d.key === key)?.label || key,
            status: v.status,
          })),
      };
      const res = await fetch(`${API}/public/worker-applications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Couldn't submit — please try again");
        return;
      }
      const data = await res.json();
      setSubmitted(data.application_id || "received");
    } catch (err) {
      toast.error("Network error — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  /* ------------------------- SUCCESS SCREEN ------------------------- */
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <header className="bg-white border-b border-slate-100">
          <div className="container mx-auto px-4 py-3 max-w-5xl">
            <span className="font-bold text-slate-900 text-lg tracking-tight">BTR Health</span>
          </div>
        </header>
        <main className="container mx-auto px-4 py-20 max-w-lg text-center">
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"
          >
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </motion.div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">You're all set, {form.first_name}!</h1>
          <p className="text-slate-600 mb-10 leading-relaxed">
            Your profile is now with the BTR Health team. We'll be in touch if a participant wants to book you.
          </p>
          <Button
            onClick={() => navigate("/")}
            className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full h-12 px-8 shadow-md shadow-[#87CEEB]/30"
            data-testid="worker-application-home-btn"
          >
            Back to home
          </Button>
        </main>
      </div>
    );
  }

  /* ------------------------- WIZARD ------------------------- */
  const StepIcon = STEPS[step].icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white/70 backdrop-blur border-b border-slate-100 sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between max-w-5xl">
          <button
            onClick={() => (step === 0 ? navigate("/worker-signup") : back())}
            className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 font-medium"
            data-testid="worker-application-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
          <span className="font-bold text-slate-900 text-base sm:text-lg tracking-tight">BTR Health</span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 pb-32 sm:pb-16 max-w-2xl">
        {/* Progress bar */}
        <div className="mb-8" data-testid="wizard-progress">
          <div className="flex items-center justify-between gap-2 mb-3">
            {STEPS.map((s, i) => {
              const done = i < step;
              const active = i === step;
              const Ic = s.icon;
              return (
                <div key={s.key} className="flex-1 flex items-center gap-2 min-w-0">
                  <div
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                      done ? "bg-[#87CEEB] border-[#87CEEB] text-white"
                      : active ? "bg-white border-[#87CEEB] text-[#3F7A99] shadow-sm"
                      : "bg-white border-slate-200 text-slate-400"
                    }`}
                    data-testid={`step-marker-${s.key}`}
                  >
                    {done ? <Check className="w-4 h-4" /> : <Ic className="w-4 h-4" />}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-0.5 flex-1 rounded-full ${done ? "bg-[#87CEEB]" : "bg-slate-200"}`} />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#3F7A99]">
              Step {step + 1} of {STEPS.length}
            </p>
            <span className="text-slate-300">•</span>
            <p className="text-[11px] text-slate-500">{STEPS[step].hint}</p>
          </div>
        </div>

        {/* Step Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -12 }}
            transition={{ duration: 0.25 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 sm:p-8"
            data-testid={`step-${STEPS[step].key}`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-[#87CEEB]/10 flex items-center justify-center">
                <StepIcon className="w-5 h-5 text-[#3F7A99]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{STEPS[step].title}</h2>
                <p className="text-xs text-slate-500">{STEPS[step].hint}</p>
              </div>
            </div>

            {step === 0 && <StepAbout {...{ form, update, errors, photoInputRef, onPhotoPick }} />}
            {step === 1 && <StepWork {...{ form, update, errors, toggleDay }} />}
            {step === 2 && <StepDocs {...{ form, update, setDoc }} />}
          </motion.div>
        </AnimatePresence>

        {/* Footer navigation — sticky on mobile */}
        <div className="fixed bottom-0 left-0 right-0 sm:static bg-white/95 sm:bg-transparent backdrop-blur sm:backdrop-blur-none border-t border-slate-100 sm:border-0 z-20">
          <div className="container mx-auto px-4 py-3 sm:p-0 sm:mt-6 max-w-2xl flex items-center justify-between gap-3">
            {step > 0 ? (
              <Button
                variant="outline"
                onClick={back}
                className="rounded-full px-5 h-11"
                data-testid="wizard-prev"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : <span />}

            {step < STEPS.length - 1 ? (
              <Button
                onClick={next}
                className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full px-6 h-11 shadow-md shadow-[#87CEEB]/30"
                data-testid="wizard-next"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={onSubmit}
                disabled={submitting}
                className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white rounded-full px-6 h-11 shadow-md shadow-[#87CEEB]/30"
                data-testid="wizard-submit"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : <>Submit profile <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </div>

        <div className="hidden sm:flex mt-6 items-center justify-center gap-4 text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-emerald-500" /> Reviewed by our team</span>
          <span className="text-slate-300">•</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> We'll be in touch</span>
        </div>
      </main>
    </div>
  );
}

/* ------------------------- STEP 1 — ABOUT ------------------------- */
function StepAbout({ form, update, errors, photoInputRef, onPhotoPick }) {
  return (
    <div className="space-y-6">
      {/* Photo */}
      <div className="flex items-center gap-4 pb-2">
        <div className="w-24 h-24 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center">
          {form.photo_data_url ? (
            <img src={form.photo_data_url} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-8 h-8 text-slate-400" />
          )}
        </div>
        <div>
          <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoPick} className="hidden" data-testid="worker-application-photo-input" />
          <Button type="button" variant="outline" onClick={() => photoInputRef.current?.click()} className="rounded-full" data-testid="worker-application-photo-btn">
            <Upload className="w-4 h-4 mr-2" />
            {form.photo_data_url ? "Change photo" : "Upload photo"}
          </Button>
          <p className="text-[11px] text-slate-400 mt-1.5">Optional — but a friendly face gets more bookings.</p>
        </div>
      </div>

      {/* Personal */}
      <SubHeading label="Personal" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldInput label="First name *" v={form.first_name} onChange={(v) => update("first_name", v)} error={errors.first_name} testid="worker-application-first-name" />
        <FieldInput label="Last name *" v={form.last_name} onChange={(v) => update("last_name", v)} error={errors.last_name} testid="worker-application-last-name" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="dob">Date of birth *</Label>
          <DobInput
            value={form.date_of_birth}
            onChange={(v) => update("date_of_birth", v)}
            invalid={!!errors.date_of_birth}
          />
          {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
        </div>
        <div>
          <Label>Gender</Label>
          <Select value={form.gender} onValueChange={(v) => update("gender", v)}>
            <SelectTrigger data-testid="worker-application-gender">
              <SelectValue placeholder="Prefer not to say" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="non_binary">Non-binary</SelectItem>
              <SelectItem value="prefer_not">Prefer not to say</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contact */}
      <SubHeading label="Contact" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FieldInput label="Email *" type="email" v={form.email} onChange={(v) => update("email", v)} error={errors.email} testid="worker-application-email" placeholder="you@example.com" />
        <FieldInput label="Mobile *" type="tel" v={form.phone} onChange={(v) => update("phone", v)} error={errors.phone} testid="worker-application-phone" placeholder="04xx xxx xxx" />
      </div>

      {/* Languages */}
      <SubHeading label="Languages" />
      <YesNoRow
        label="Do you speak another language besides English?"
        value={form.speaks_other_language}
        onChange={(v) => {
          update("speaks_other_language", v);
          if (!v) update("other_language", "");
        }}
        testid="worker-application-other-lang"
      />
      {form.speaks_other_language === true && (
        <div>
          <Label htmlFor="other_language">Which language(s)?</Label>
          <Input
            id="other_language"
            value={form.other_language}
            onChange={(e) => update("other_language", e.target.value)}
            placeholder="e.g. Tamil, Vietnamese"
            data-testid="worker-application-other-language"
          />
        </div>
      )}

      {/* Address */}
      <SubHeading label="Home address" />
      <FieldInput placeholder="Street address *" v={form.address_line} onChange={(v) => update("address_line", v)} error={errors.address_line} testid="worker-application-address-line" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <FieldInput placeholder="Suburb *" v={form.suburb} onChange={(v) => update("suburb", v)} error={errors.suburb} testid="worker-application-suburb" />
        <div>
          <Select value={form.state} onValueChange={(v) => update("state", v)}>
            <SelectTrigger data-testid="worker-application-state" aria-invalid={!!errors.state}>
              <SelectValue placeholder="State *" />
            </SelectTrigger>
            <SelectContent>
              {["VIC", "NSW", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
        </div>
        <FieldInput placeholder="Postcode *" v={form.postcode} onChange={(v) => update("postcode", v)} error={errors.postcode} testid="worker-application-postcode" />
      </div>
    </div>
  );
}

function SubHeading({ label }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <div className="h-1 w-1 rounded-full bg-[#87CEEB]" />
      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="h-px bg-slate-100 flex-1" />
    </div>
  );
}

/**
 * DobInput — three-field DD / MM / YYYY entry with auto-advance.
 * Emits ISO `YYYY-MM-DD` (or "" if incomplete) through `onChange`.
 */
function DobInput({ value, onChange, invalid }) {
  // Split incoming ISO value into parts once
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  const [dd, setDd] = useState(iso ? iso[3] : "");
  const [mm, setMm] = useState(iso ? iso[2] : "");
  const [yyyy, setYyyy] = useState(iso ? iso[1] : "");
  const mRef = useRef(null);
  const yRef = useRef(null);

  const emit = (d, m, y) => {
    if (d.length === 2 && m.length === 2 && y.length === 4) {
      onChange(`${y}-${m}-${d}`);
    } else if (!d && !m && !y) {
      onChange("");
    } else {
      onChange(""); // incomplete — clear iso so validation catches it
    }
  };

  const onlyDigits = (s, max) => s.replace(/\D/g, "").slice(0, max);

  return (
    <div
      className={`flex items-center gap-2 rounded-md border bg-white px-3 py-2 h-10 ${invalid ? "border-red-400" : "border-input"}`}
      data-testid="worker-application-dob"
    >
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={dd}
        placeholder="DD"
        maxLength={2}
        onChange={(e) => {
          const v = onlyDigits(e.target.value, 2);
          setDd(v);
          if (v.length === 2) mRef.current?.focus();
          emit(v, mm, yyyy);
        }}
        className="w-8 text-center text-sm bg-transparent focus:outline-none placeholder:text-slate-300"
        aria-label="Day"
        data-testid="worker-application-dob-day"
      />
      <span className="text-slate-300">/</span>
      <input
        ref={mRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={mm}
        placeholder="MM"
        maxLength={2}
        onChange={(e) => {
          const v = onlyDigits(e.target.value, 2);
          setMm(v);
          if (v.length === 2) yRef.current?.focus();
          emit(dd, v, yyyy);
        }}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && !mm) {
            e.preventDefault();
            const el = e.currentTarget.parentElement.querySelectorAll("input")[0];
            el?.focus();
          }
        }}
        className="w-8 text-center text-sm bg-transparent focus:outline-none placeholder:text-slate-300"
        aria-label="Month"
        data-testid="worker-application-dob-month"
      />
      <span className="text-slate-300">/</span>
      <input
        ref={yRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={yyyy}
        placeholder="YYYY"
        maxLength={4}
        onChange={(e) => {
          const v = onlyDigits(e.target.value, 4);
          setYyyy(v);
          emit(dd, mm, v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && !yyyy) {
            e.preventDefault();
            mRef.current?.focus();
          }
        }}
        className="w-14 text-center text-sm bg-transparent focus:outline-none placeholder:text-slate-300"
        aria-label="Year"
        data-testid="worker-application-dob-year"
      />
      <span className="ml-auto text-[11px] text-slate-300">DD / MM / YYYY</span>
    </div>
  );
}

/* ------------------------- STEP 2 — WORK ------------------------- */
function StepWork({ form, update, errors, toggleDay }) {
  return (
    <div className="space-y-6">
      <SubHeading label="About your work" />
      <div>
        <Label htmlFor="bio">Short bio</Label>
        <Textarea id="bio" value={form.bio} onChange={(e) => update("bio", e.target.value)} rows={4} placeholder="Tell participants a little about yourself, your background and why you love this work." data-testid="worker-application-bio" />
        <p className="text-[11px] text-slate-400 mt-1">Optional — you can always add this later.</p>
      </div>

      <div>
        <Label>Years of experience</Label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1.5" role="radiogroup">
          {EXPERIENCE_LEVELS.map((opt) => {
            const active = form.experience_level === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => update("experience_level", active ? "" : opt.value)}
                className={`px-3 py-2.5 text-xs font-medium rounded-xl border transition-colors text-center ${
                  active
                    ? "bg-[#87CEEB] border-[#87CEEB] text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-700 hover:border-[#87CEEB] hover:text-[#3F7A99]"
                }`}
                data-testid={`worker-application-experience-${opt.value}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Label htmlFor="qualifications">Qualifications</Label>
        <Textarea
          id="qualifications"
          value={form.qualifications}
          onChange={(e) => update("qualifications", e.target.value)}
          rows={3}
          placeholder="e.g. Certificate III in Individual Support · Diploma of Community Services · Enrolled Nurse · Cert IV Disability"
          data-testid="worker-application-qualifications"
        />
        <p className="text-[11px] text-slate-400 mt-1">List each qualification on a new line. Leave blank if none yet.</p>
      </div>

      <SubHeading label="Availability" />
      <div>
        <div className="flex flex-wrap gap-2" data-testid="worker-application-availability-days">
          {DAYS.map((d) => {
            const active = form.availability_days.includes(d.key);
            return (
              <button
                key={d.key}
                type="button"
                onClick={() => toggleDay(d.key)}
                className={`flex-1 min-w-[54px] h-11 rounded-xl text-sm font-semibold border transition-colors ${
                  active
                    ? "bg-[#87CEEB] border-[#87CEEB] text-white shadow-sm"
                    : "bg-white border-slate-200 text-slate-600 hover:border-[#87CEEB]"
                }`}
                data-testid={`worker-application-day-${d.key}`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        <Input
          value={form.availability_notes}
          onChange={(e) => update("availability_notes", e.target.value)}
          placeholder="Optional: preferred hours, e.g. 'Weekday mornings and Sunday nights'"
          className="mt-3"
          data-testid="worker-application-availability-notes"
        />
      </div>

      <SubHeading label="Insurance & ABN" />
      <div className="space-y-2">
        <YesNoRow
          label="Professional Indemnity"
          value={form.has_professional_indemnity}
          onChange={(v) => update("has_professional_indemnity", v)}
          testid="worker-application-insurance-pi"
        />
        <YesNoRow
          label="Public Liability"
          value={form.has_public_liability}
          onChange={(v) => update("has_public_liability", v)}
          testid="worker-application-insurance-pl"
        />
        <YesNoRow
          label="I have an ABN"
          value={form.has_abn}
          onChange={(v) => update("has_abn", v)}
          testid="worker-application-has-abn"
        />
        {form.has_abn === true && (
          <Input
            value={form.abn}
            onChange={(e) => update("abn", e.target.value)}
            placeholder="ABN — XX XXX XXX XXX"
            data-testid="worker-application-abn"
          />
        )}
      </div>

      <div>
        <Label htmlFor="notes">Anything else? (optional)</Label>
        <Textarea id="notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} placeholder="Anything you'd like the BTR team to know." data-testid="worker-application-notes" />
      </div>
    </div>
  );
}

/* ------------------------- STEP 3 — DOCS ------------------------- */
function StepDocs({ form, update, setDoc }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 -mt-2">For each item, just let us know if you have it. We'll ask for copies later if you're a match.</p>
      {DOCUMENT_TYPES.map((doc) => {
        const state = form.documents[doc.key];
        const yes = state.status === "have";
        const no = state.status === "dont_have";
        return (
          <div
            key={doc.key}
            className={`rounded-xl border p-3.5 transition-colors ${
              yes ? "bg-emerald-50/50 border-emerald-200"
              : no ? "bg-slate-50 border-slate-200"
              : "border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`worker-application-doc-${doc.key}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${
                  yes ? "bg-emerald-100" : "bg-slate-100"
                }`}>
                  <FileText className={`w-4 h-4 ${yes ? "text-emerald-600" : "text-slate-500"}`} />
                </div>
                <p className="text-sm font-semibold text-slate-900">{doc.label}</p>
              </div>
              <div className="inline-flex rounded-full bg-slate-100 p-0.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setDoc(doc.key, { status: "have" })}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    yes ? "bg-[#87CEEB] text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`worker-application-doc-${doc.key}-yes`}
                  aria-pressed={yes}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setDoc(doc.key, { status: "dont_have" })}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    no ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`worker-application-doc-${doc.key}-no`}
                  aria-pressed={no}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Listing consent */}
      <button
        type="button"
        onClick={() => update("consent_listing", !form.consent_listing)}
        className={`w-full text-left mt-4 rounded-2xl border p-4 flex items-start gap-3 transition-colors ${
          form.consent_listing
            ? "bg-[#87CEEB]/10 border-[#87CEEB]"
            : "bg-white border-slate-200 hover:border-slate-300"
        }`}
        data-testid="worker-application-consent"
        aria-pressed={form.consent_listing}
      >
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${
          form.consent_listing ? "border-[#87CEEB] bg-[#87CEEB] text-white" : "border-slate-300"
        }`}>
          {form.consent_listing && <Check className="w-3 h-3" />}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">I consent to being listed on the BTR Health website</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            You agree for BTR Health to display your profile (name, photo, bio, languages and general location) on our public directory so participants can find you. You can ask us to remove your listing anytime.
          </p>
        </div>
      </button>
    </div>
  );
}

/* ------------------------- Small helpers ------------------------- */
function FieldInput({ label, placeholder, type = "text", v, onChange, error, testid }) {
  return (
    <div>
      {label && <Label>{label}</Label>}
      <Input
        type={type}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        aria-invalid={!!error}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

function ToggleCard({ label, checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`text-left px-3.5 py-3 rounded-xl border transition-colors flex items-center gap-3 ${
        checked
          ? "bg-[#87CEEB]/10 border-[#87CEEB] text-[#3F7A99]"
          : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
      }`}
      data-testid={testid}
      aria-pressed={checked}
    >
      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
        checked ? "border-[#87CEEB] bg-[#87CEEB] text-white" : "border-slate-300"
      }`}>
        {checked && <Check className="w-3 h-3" />}
      </div>
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function YesNoRow({ label, value, onChange, testid }) {
  const opts = [
    { v: true,  txt: "Yes" },
    { v: false, txt: "No"  },
  ];
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5"
      data-testid={testid}
    >
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <div className="inline-flex rounded-full bg-slate-100 p-0.5">
        {opts.map((o) => {
          const active = value === o.v;
          return (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                active
                  ? o.v
                    ? "bg-[#87CEEB] text-white shadow-sm"
                    : "bg-slate-700 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`${testid}-${o.v ? "yes" : "no"}`}
              aria-pressed={active}
            >
              {o.txt}
            </button>
          );
        })}
      </div>
    </div>
  );
}
